/** Small DOM helpers and the HUD's icons (the board's own SVGs where it has them). */

export function el<K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  cls = '',
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = doc.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function add<T extends Element>(parent: Element, ...children: T[]): T {
  for (const c of children) parent.appendChild(c);
  return children[children.length - 1] as T;
}

const SVG = 'http://www.w3.org/2000/svg';

function svg(doc: Document, size: number, box: number, attrs: Record<string, string> = {}): SVGSVGElement {
  const s = doc.createElementNS(SVG, 'svg');
  s.setAttribute('width', String(size));
  s.setAttribute('height', String(size));
  s.setAttribute('viewBox', `0 0 ${box} ${box}`);
  s.setAttribute('aria-hidden', 'true');
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
  return s;
}

function child(doc: Document, parent: SVGElement, tag: string, attrs: Record<string, string>): SVGElement {
  const c = doc.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) c.setAttribute(k, v);
  parent.appendChild(c);
  return c;
}

/** Frame 01's jet icon (22 px, viewBox 24). */
export function jetIcon(doc: Document): SVGSVGElement {
  const s = svg(doc, 22, 24);
  child(doc, s, 'path', {
    d: 'M12 2 L13.2 7 L13.5 10 L21 14 L21 15.6 L13.4 14.2 L13.2 18 L15.6 20.2 L15.6 21.4 L12 20.6 L8.4 21.4 L8.4 20.2 L10.8 18 L10.6 14.2 L3 15.6 L3 14 L10.5 10 L10.8 7 Z',
    fill: '#f4f1ea',
  });
  return s;
}

/** Frame 04's pause icon (18 px). */
export function pauseIcon(doc: Document): SVGSVGElement {
  const s = svg(doc, 18, 18, { fill: 'none', stroke: '#f4f1ea', 'stroke-width': '2.4', 'stroke-linecap': 'round' });
  child(doc, s, 'line', { x1: '6', y1: '3', x2: '6', y2: '15' });
  child(doc, s, 'line', { x1: '12', y1: '3', x2: '12', y2: '15' });
  return s;
}

/** Settings: three sliders, drawn to match the pause icon's weight. */
export function settingsIcon(doc: Document): SVGSVGElement {
  const s = svg(doc, 18, 18, { fill: 'none', stroke: '#f4f1ea', 'stroke-width': '2', 'stroke-linecap': 'round' });
  for (const [y, k] of [
    [4, 11.5],
    [9, 6],
    [14, 10],
  ] as const) {
    child(doc, s, 'line', { x1: '2.5', y1: String(y), x2: '15.5', y2: String(y) });
    child(doc, s, 'circle', { cx: String(k), cy: String(y), r: '2', fill: '#0b0f12' });
  }
  return s;
}

/** Frame 04's fire crosshair (30 px). */
export function crosshairIcon(doc: Document): SVGSVGElement {
  const s = svg(doc, 30, 30, { fill: 'none', stroke: '#ffe2b0', 'stroke-width': '2.2', 'stroke-linecap': 'round' });
  child(doc, s, 'circle', { cx: '15', cy: '15', r: '9' });
  child(doc, s, 'line', { x1: '15', y1: '2', x2: '15', y2: '8' });
  child(doc, s, 'line', { x1: '15', y1: '22', x2: '15', y2: '28' });
  child(doc, s, 'line', { x1: '2', y1: '15', x2: '8', y2: '15' });
  child(doc, s, 'line', { x1: '22', y1: '15', x2: '28', y2: '15' });
  return s;
}
